import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Menu, X, Phone } from "lucide-react";
import EcaBadge from "@/components/EcaBadge";

const linkCls =
  "text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:text-copper";

const MENU_ZIGZAG =
  "0,30 120,10 240,42 360,12 480,40 600,14 720,44 840,10 960,40 1080,16 1200,42 1320,12 1440,30";

const LOGO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_0d237708-2939-4749-85f1-51c7a8f51e7c/artifacts/0wjcv2em_IMG_9205.jpeg";

export const Wordmark = ({ large = false }) => (
  <img
    src={LOGO}
    alt="Exchange Electrical"
    className={`w-auto ${large ? "h-12 md:h-11" : "h-9"}`}
  />
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const goToSection = (id) => {
    setOpen(false);
    window.__lenis?.start();
    if (!isHome) {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(id, { offset: -80 }), 500);
    } else {
      window.__lenis?.scrollTo(id, { offset: -80 });
    }
  };

  const goHome = () => {
    setOpen(false);
    window.__lenis?.start();
    if (!isHome) {
      navigate("/");
    } else {
      window.__lenis?.scrollTo(0);
    }
  };

  const goToPath = (path) => {
    setOpen(false);
    window.__lenis?.start();
    navigate(path);
  };

  const goBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const menuItems = [
    { label: "Home", action: goHome },
    { label: "Services", action: () => goToPath("/services") },
    { label: "Work", action: () => goToSection("#work") },
    { label: "About", action: () => goToSection("#about") },
    { label: "Contact", action: () => goToSection("#contact") },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b-2 border-ink/10 bg-white/95 backdrop-blur-md">
        <div className="relative mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10 md:py-5">
          {!isHome && (
            <button
              onClick={goBack}
              data-testid="nav-back-button"
              aria-label="Back to homepage"
              className="flex h-9 w-9 items-center justify-center text-ink transition-colors hover:text-copper md:hidden"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            </button>
          )}
          <nav className="hidden items-center gap-8 md:flex">
            <button data-testid="nav-home-link" onClick={goHome} className={linkCls}>Home</button>
            <button
              data-testid="nav-services-link"
              onClick={() => goToSection("#services")}
              className={`${linkCls} ${
                location.pathname === "/services"
                  ? "underline decoration-copper decoration-[3px] underline-offset-8"
                  : ""
              }`}
            >
              Services
            </button>
            <button data-testid="nav-work-link" onClick={() => goToSection("#work")} className={linkCls}>Work</button>
          </nav>
          <Link
            to="/"
            data-testid="nav-logo-link"
            className={`flex items-center ${
              isHome ? "md:absolute md:left-1/2 md:-translate-x-1/2" : "absolute left-1/2 -translate-x-1/2"
            }`}
          >
            <EcaBadge />
          </Link>
          <nav className="flex items-center gap-8">
            <button
              data-testid="nav-about-link"
              onClick={() => goToSection("#about")}
              className={`${linkCls} hidden md:block`}
            >
              About
            </button>
            <button
              data-testid="nav-contact-link"
              onClick={() => goToSection("#contact")}
              className={`${linkCls} hidden md:block`}
            >
              Contact
            </button>
            <Link
              to="/quote"
              data-testid="nav-quote-button"
              className={`group hidden items-center gap-1.5 px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors md:flex ${
                location.pathname === "/quote"
                  ? "bg-copper text-ink"
                  : "bg-petrol text-white hover:bg-copper hover:text-ink"
              }`}
            >
              Quote
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="flex h-12 w-12 items-center justify-center text-ink transition-colors hover:text-copper md:hidden"
            >
              <Menu className="h-8 w-8" strokeWidth={2} />
            </button>
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#102630] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="blueprint-grid absolute inset-0" aria-hidden="true" />
            <div className="relative flex items-center justify-between px-5 py-4">
              <Wordmark large />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center text-white transition-colors hover:text-copper"
              >
                <X className="h-7 w-7" strokeWidth={2} />
              </button>
            </div>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="relative mt-4 h-8 w-full" aria-hidden="true">
              <motion.polyline
                points={MENU_ZIGZAG}
                fill="none"
                stroke="#c8925a"
                strokeWidth="3"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              />
            </svg>
            <nav className="relative mt-10 flex flex-col gap-2 px-8">
              {menuItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.09, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <button
                    onClick={item.action}
                    data-testid={`mobile-menu-${item.label.toLowerCase()}`}
                    className="flex items-baseline gap-4 font-display text-5xl font-black uppercase tracking-tight text-white transition-colors hover:text-copper"
                  >
                    <span className="font-body text-sm font-medium text-copper">0{i + 1}</span>
                    {item.label}
                  </button>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative mt-auto space-y-4 px-8 pb-10"
            >
              <button
                onClick={() => goToPath("/quote")}
                data-testid="mobile-menu-quote-button"
                className="group flex w-full items-center justify-center gap-2 bg-copper px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white"
              >
                Free Quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
              </button>
              <a
                href="tel:0490425408"
                data-testid="mobile-menu-call-link"
                className="flex items-center justify-center gap-2 text-sm uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-copper"
              >
                <Phone className="h-4 w-4 text-copper" strokeWidth={1.5} />
                0490 425 408 — Derek
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
